import { DecoratorAuthorization } from '@seddi/ui-decorator-authorization';
import { type FC, useEffect, useState } from 'react';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { useDispatch } from 'react-redux';

import { AppLoadingPage } from './components/AppLoadingPage';
import ReCaptchaGuard from './components/ReCaptchaGuard';
import { HOST_ADDRESS, NODE_ENV, RECAPTCHA_SCORE_KEY } from './constants/env';
import {
  checkAuthentication,
  getApiAddress,
  isUserAuthenticated,
  setAuthenticationStatus,
} from './core/apiConfig';
import { AccessModes, AccessModesGroups } from './domain/accessMode/accessManagerTypes';
import { setAccessMode } from './store/accessMode';
import { App } from './App';

type SessionRefresherProps = {
  baseUrl: string;
};

const SessionRefresher: FC<SessionRefresherProps> = ({ baseUrl }) => {
  const isEnabled = NODE_ENV === 'production' && !baseUrl.includes('localhost');

  return isEnabled ? (
    <iframe
      src={`${baseUrl}/?gcp-iap-mode=SESSION_REFRESHER`}
      style={{
        border: 'none',
        height: 0,
        left: 0,
        position: 'absolute',
        top: 0,
        visibility: 'hidden',
        width: 0,
      }}
      title="session-refresher"
    />
  ) : null;
};

const useAppWrapper = () => {
  const dispatch = useDispatch();
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    // Check authentication before rendering anything
    checkAuthentication().then(isAuthenticated => {
      setAuthenticationStatus(isAuthenticated);

      // Set PUBLIC_PERMISSION access mode
      dispatch(
        setAccessMode({
          group: AccessModesGroups.PUBLIC_PERMISSION,
          accessMode: isAuthenticated ? AccessModes.LICENSED_USER : AccessModes.PUBLIC_USER,
        }),
      );

      setAuthChecked(true);
    });
  }, [dispatch]);

  return { authChecked };
};

export const AppWrapper: FC = () => {
  const { authChecked } = useAppWrapper();

  // Show loading page while checking authentication
  if (!authChecked) {
    return <AppLoadingPage />;
  }

  const apiAddress = getApiAddress();
  const isAuthenticated = isUserAuthenticated();

  return (
    <>
      <SessionRefresher baseUrl={apiAddress} />
      <DecoratorAuthorization
        backgroundImage={`${HOST_ADDRESS}/static/images/background_image.webp`}
        baseUrl={apiAddress}
        brandLogo={`${HOST_ADDRESS}/static/images/seddi_decorator.svg`}
        checkLicense={true}
        hostUrl={HOST_ADDRESS}
      >
        {isAuthenticated ? (
          // Authenticated users: render App directly
          <App />
        ) : (
          // Non-authenticated users: wrap with reCaptcha protection
          <GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_SCORE_KEY} useEnterprise={true}>
            <ReCaptchaGuard>
              <App />
            </ReCaptchaGuard>
          </GoogleReCaptchaProvider>
        )}
      </DecoratorAuthorization>
    </>
  );
};
